
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Camera, ImageUp, Inbox, Trash2, type LucideIcon } from 'lucide-react';
import { formatPhone } from '@/shared/lib/phone';
import { toast } from 'sonner';
import { DetailBar } from '@/widgets/DetailBar';
import { TicketCard } from '@/widgets/TicketCard';
import { useAuthStore } from '@/features/auth';
import {
  apiErrorText,
  foldersApi,
  userProfileApi,
  usersApi,
  type FolderGrant,
  type PublicUserProfile,
  type UserProfile,
} from '@/shared/api';
import { FOLDER_ACCESS_LABELS } from '@/shared/api/forms';
import { ticketsApi } from '@/shared/api/tickets';
import type { Ticket } from '@/entities/ticket';
import { fullNameOf } from '@/entities/user';
import {
  AssigneeIcon,
  EmailIcon,
  KpiIcon,
  PhoneIcon,
  RatingIcon,
  ResolvedIcon,
  SYSTEM_ROLE_LABELS,
  TicketIcon,
  type SystemRole,
} from '@/shared/config';
import {
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  confirmDialog,
  EmptyState,
  ErrorState,
  MediaViewer,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageMotion,
  Skeleton,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { avatarColor } from '@/shared/lib/avatar';
import { resolveApiUrl } from '@/shared/lib/config';
import { AvatarCropDialog } from './AvatarCropDialog';
import styles from './UserProfile.module.css';

type Tone = 'gold' | 'positive' | 'info' | 'accent';

const ToneIcon: React.FC<{ icon: LucideIcon; tone: Tone }> = ({ icon: Icon, tone }) => (
  <span className={cn(styles.toneIcon, styles[`tone-${tone}`])} aria-hidden>
    <Icon size={16} />
  </span>
);

const Stat: React.FC<{
  icon: LucideIcon;
  tone: Tone;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  accent?: boolean;
  children?: React.ReactNode;
}> = ({ icon, tone, label, value, hint, accent, children }) => (
  <div className={styles.stat}>
    <div className={styles.statTop}>
      <ToneIcon icon={icon} tone={tone} />
      <span className={styles.statLabel}>{label}</span>
    </div>
    <span className={cn(styles.statValue, accent && styles.statAccent)}>{value}</span>
    {children}
    {hint && <span className={styles.statHint}>{hint}</span>}
  </div>
);

const Stars: React.FC<{ value: number }> = ({ value }) => (
  <span className={styles.stars} aria-hidden>
    <span className={styles.starsTrack}>
      {Array.from({ length: 5 }, (_, i) => (
        <RatingIcon key={i} size={14} />
      ))}
    </span>
    <span className={styles.starsFill} style={{ width: `${(Math.max(0, Math.min(5, value)) / 5) * 100}%` }}>
      {Array.from({ length: 5 }, (_, i) => (
        <RatingIcon key={i} size={14} fill="currentColor" />
      ))}
    </span>
  </span>
);

const Ring: React.FC<{ part: number; total: number }> = ({ part, total }) => {
  const share = total > 0 ? part / total : 0;
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <span className={styles.ring} aria-hidden>
      <svg viewBox="0 0 52 52" className={styles.ringSvg}>
        <circle cx="26" cy="26" r={r} className={styles.ringTrack} />
        <circle
          cx="26"
          cy="26"
          r={r}
          className={styles.ringValue}
          strokeDasharray={`${c * share} ${c}`}
        />
      </svg>
      <span className={styles.ringLabel}>{Math.round(share * 100)}%</span>
    </span>
  );
};

const num = (value: number): string =>
  (Math.round(value * 100) / 100).toLocaleString('ru-RU');

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const sinceLabel = (iso: string): string => {
  const d = new Date(iso);
  return `${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()}`;
};

const ONLINE_WINDOW_MS = 5 * 60_000;

const seenRecently = (iso: string): boolean => Date.now() - new Date(iso).getTime() < ONLINE_WINDOW_MS;

const lastSeenLabel = (iso: string): string => {
  const d = new Date(iso);
  const minutes = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (minutes < 60) return `${Math.max(minutes, 1)} мин назад`;
  const time = d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return `сегодня в ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `вчера в ${time}`;
  return d.toLocaleDateString('ru', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
};

const MAX_AVATAR_BYTES = 10 * 1024 * 1024;

export const UserProfilePage: React.FC = () => {
  const { id = 'me' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const setMyAvatarUrl = useAuthStore((s) => s.setAvatarUrl);
  const isStaff = hasPermission('view_tickets');

  const [profile, setProfile] = useState<UserProfile | PublicUserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'created' | 'assigned'>('overview');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [grants, setGrants] = useState<FolderGrant[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [bannerFailed, setBannerFailed] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLHeadingElement | null>(null);
  const [nameHidden, setNameHidden] = useState(false);
  const afterMenuRef = useRef<(() => void) | null>(null);

  const targetId = id === 'me' ? currentUserId ?? 'me' : id;
  const isSelf = targetId === currentUserId;
  const full = profile && !('limited' in profile) ? profile : null;

  const backTo = (location.state as { from?: string } | null)?.from ?? (isStaff ? '/kpi' : '/');
  const canGoBack = location.key !== 'default';
  const backBar = (
    <DetailBar
      className={styles.backBar}
      iconOnly
      title={
        profile && (
          <>
            <Avatar
              className={styles.barAvatar}
              name={profile.user.displayName}
              seed={String(targetId)}
              src={profile.user.avatarUrl}
            />
            <span className={styles.barName}>{fullNameOf(profile.user)}</span>
          </>
        )
      }
      titleVisible={nameHidden}
      backTo={backTo}
      backLabel="Назад"
      onBack={(e) => {
        if (canGoBack && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
          e.preventDefault();
          navigate(-1);
        }
      }}
    />
  );

  useEffect(() => setTab('overview'), [id]);

  useEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setNameHidden(!e.isIntersecting), {
      rootMargin: '-128px 0px 0px 0px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [profile]);

  useEffect(() => {
    let alive = true;
    setProfile(null);
    setError(null);
    setTickets([]);
    setGrants([]);
    userProfileApi
      .get(id)
      .then((res) => alive && setProfile(res.data ?? null))
      .catch((err) => alive && setError(apiErrorText(err, 'Не удалось загрузить профиль')));
    return () => {
      alive = false;
    };
  }, [id]);

  const isEngineer = (profile?.groups.length ?? 0) > 0 || (full?.kpi.totalTicketsClosed ?? 0) > 0;

  const canSeeHistory = isSelf || isStaff;
  useEffect(() => {
    if (!profile || !canSeeHistory || tab === 'overview') return;
    let alive = true;
    setTickets([]);
    setTicketsLoading(true);
    const params =
      tab === 'created'
        ? isSelf && !isStaff
          ? { limit: 20 }
          : { creatorId: targetId, view: 'pool' as const, limit: 20 }
        : { assigneeId: targetId, view: 'pool' as const, limit: 20 };
    ticketsApi
      .list(params)
      .then((res) => alive && setTickets((res.data ?? []) as Ticket[]))
      .catch(() => alive && setTickets([]))
      .finally(() => alive && setTicketsLoading(false));
    return () => {
      alive = false;
    };
  }, [profile, tab, canSeeHistory, isSelf, isStaff, targetId]);

  useEffect(() => {
    if (!full) return;
    let alive = true;
    setGrants([]);
    void foldersApi
      .grantsOfUser(targetId)
      .then((res) => alive && setGrants(res.data))
      .catch(() => alive && setGrants([]));
    return () => {
      alive = false;
    };
  }, [full, targetId]);

  const roleChips = useMemo(
    () =>
      (full?.roles ?? [])
        .filter((r): r is SystemRole => r in SYSTEM_ROLE_LABELS)
        .map((r) => ({ key: r, label: SYSTEM_ROLE_LABELS[r] })),
    [full],
  );

  const applyAvatar = (avatarUrl: string | null) => {
    setProfile((p) => (p && !('limited' in p) ? { ...p, user: { ...p.user, avatarUrl } } : p));
    setMyAvatarUrl(avatarUrl);
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Нужна картинка — JPEG, PNG или WebP');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Фото больше 10 МБ');
      return;
    }
    setCropFile(file);
  };

  const saveAvatar = async (blob: Blob) => {
    const { avatarUrl } = await usersApi.uploadAvatar(blob);
    applyAvatar(avatarUrl);
    setCropFile(null);
    toast.success('Фото обновлено');
  };

  const removeAvatar = async () => {
    const ok = await confirmDialog({
      title: 'Убрать фото?',
      description: 'Вместо него снова будут инициалы.',
      confirmLabel: 'Убрать',
      danger: true,
    });
    if (!ok) return;
    try {
      await usersApi.removeAvatar();
      applyAvatar(null);
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось убрать фото'));
    }
  };

  const afterMenuClose = (open: boolean) => {
    if (open) return;
    const run = afterMenuRef.current;
    afterMenuRef.current = null;
    run?.();
  };

  if (error)
    return (
      <PageMotion>
        <main className={styles.page}>
          {backBar}
          <ErrorState message={error} />
        </main>
      </PageMotion>
    );

  if (!profile)
    return (
      <PageMotion>
        <main className={styles.page}>
          {backBar}
          <Card padded={false} className={styles.profileCard}>
            <Skeleton className={styles.banner} />
            <div className={styles.profileBody}>
              <section className={styles.header}>
                <span className={cn(styles.avatarSlot, styles.avatar, styles.avatarBone)} aria-hidden>
                  <Skeleton circle height="100%" />
                </span>
                <div className={styles.name}>
                  <Skeleton className={styles.textBone} width="12rem" />
                  {isSelf && <Skeleton className={styles.selfTagBone} width="1.75rem" />}
                </div>
                <div className={styles.headerMore}>
                  <p className={styles.status}>
                    <Skeleton className={styles.textBone} width="4rem" />
                  </p>
                  <div className={styles.roles}>
                    {['6.5rem', '4.5rem'].map((w) => (
                      <Skeleton key={w} className={styles.roleBone} width={w} />
                    ))}
                  </div>
                  <p className={styles.subtitle}>
                    <Skeleton className={styles.textBone} width="14rem" />
                  </p>
                  <p className={styles.meta}>
                    <Skeleton className={styles.textBone} width="11rem" />
                  </p>
                </div>
                <div className={styles.contacts}>
                  {['11rem', '9rem'].map((w) => (
                    <Skeleton key={w} className={styles.contactBone} width={w} />
                  ))}
                </div>
              </section>
            </div>
          </Card>
          <Skeleton className={styles.tabsBone} width="18.5rem" />
          <Card className={`${styles.cardBone} ${styles.overviewCard}`}>
            <section className={styles.statGroup}>
              <div className={styles.statGroupHead}>
                <Skeleton className={styles.textBone} width="7rem" />
              </div>
              {isStaff && (
                <div className={styles.stats}>
                  {[0, 1, 2].map((i) => (
                    <div key={i} className={styles.stat}>
                      <Skeleton className={cn(styles.textBone, styles.statLabel)} width="7rem" />
                      <Skeleton className={cn(styles.textBone, styles.statValue)} width="3rem" />
                      <Skeleton className={cn(styles.textBone, styles.statHint)} width="4rem" />
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.wideStats}>
                <div className={styles.wideStat}>
                  <Skeleton className={cn(styles.textBone, styles.statLabel)} width="8rem" />
                  <Skeleton className={styles.barBone} />
                </div>
              </div>
            </section>
          </Card>
        </main>
      </PageMotion>
    );

  const { user } = profile;
  const ticketStats = full?.ticketStats;
  const kpi = full?.kpi;
  const hasPhoto = Boolean(user.avatarUrl);

  const ticketList = ticketsLoading ? (
    <div className={styles.list}>
      { }
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} height={isStaff ? 136 : 124} radius="var(--radius-4xl)" />
      ))}
    </div>
  ) : tickets.length === 0 ? (
    <EmptyState
      Icon={Inbox}
      title="Заявок нет"
      hint={tab === 'created' ? 'Ни одной созданной заявки.' : 'Ни одной заявки в работе.'}
    />
  ) : (
    <div className={styles.list}>
      {tickets.map((t) => (
        <TicketCard key={t.id} ticket={t} showDetails={isStaff} />
      ))}
    </div>
  );

  const avatar = (
    <Avatar
      className={styles.avatar}
      name={user.displayName}
      seed={String(targetId)}
      src={user.avatarUrl}
    />
  );

  const contacts = [
    user.email && { key: 'mail', icon: EmailIcon, text: user.email, done: 'Почта скопирована' },
    user.phone && { key: 'tel', icon: PhoneIcon, text: formatPhone(user.phone), done: 'Номер скопирован' },
  ].filter(Boolean) as { key: string; icon: LucideIcon; text: string; done: string }[];

  const copyContact = async (text: string, done: string) => {
    if (await copyToClipboard(text)) toast.success(done);
    else toast.error('Не удалось скопировать');
  };

  const bannerPhoto = user.avatarUrl && bannerFailed !== user.avatarUrl ? user.avatarUrl : null;
  const subtitle = [
    ...profile.groups.map((g) => (
      <span key={`g${g.id}`} title={('publicName' in g && g.publicName) || undefined}>
        {g.name}
      </span>
    )),
    user.department && (
      <span key="dep" title={user.orgPath?.join(' / ') || undefined}>
        {user.department}
      </span>
    ),
  ].filter(Boolean);
  const createdAt = full?.user.createdAt;
  const lastLoginAt = full?.lastLoginAt ?? null;
  const meta = [createdAt && `в системе с ${sinceLabel(createdAt)}`].filter(Boolean) as string[];
  const online = full != null && (isSelf || (lastLoginAt != null && seenRecently(lastLoginAt)));
  const status = online ? 'в сети' : lastLoginAt ? `был(а) ${lastSeenLabel(lastLoginAt)}` : null;

  return (
    <PageMotion>
      <main className={styles.page}>
        {backBar}
        <Card padded={false} className={styles.profileCard}>
          <div className={styles.banner} aria-hidden>
            {bannerPhoto ? (
              <img
                className={styles.bannerPhoto}
                src={resolveApiUrl(bannerPhoto)}
                alt=""
                decoding="async"
                draggable={false}
                onError={() => setBannerFailed(bannerPhoto)}
              />
            ) : (
              <span className={styles.bannerColor} style={{ background: avatarColor(String(targetId)) }} />
            )}
          </div>
          <div className={styles.profileBody}>
            <section className={styles.header}>
              <div className={styles.avatarSlot}>
                {isSelf ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif"
                      className={styles.fileInput}
                      onChange={onFileChosen}
                      tabIndex={-1}
                      aria-hidden
                    />
                    { }
                    {hasPhoto ? (
                      <>
                        <button
                          type="button"
                          className={cn(styles.avatarButton, styles.avatarZoom)}
                          aria-label="Открыть фото"
                          onClick={() => setViewerOpen(true)}
                        >
                          {avatar}
                        </button>
                        <Menu onOpenChangeComplete={afterMenuClose}>
                          <MenuTrigger render={<button type="button" className={styles.avatarBadge} aria-label="Фото профиля: сменить или убрать" />}>
                            <Camera size={14} />
                          </MenuTrigger>
                          <MenuContent align="start" sideOffset={8}>
                            <MenuItem onClick={pickFile}>
                              <ImageUp size={15} aria-hidden />
                              Сменить фото
                            </MenuItem>
                            <MenuSeparator />
                            <MenuItem
                              variant="danger"
                              onClick={() => {
                                afterMenuRef.current = () => void removeAvatar();
                              }}
                            >
                              <Trash2 size={15} aria-hidden />
                              Убрать фото
                            </MenuItem>
                          </MenuContent>
                        </Menu>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.avatarButton}
                          aria-label="Загрузить фото профиля"
                          onClick={pickFile}
                        >
                          {avatar}
                        </button>
                        <button type="button" className={styles.avatarBadge} aria-label="Загрузить фото профиля" onClick={pickFile}>
                          <Camera size={14} />
                        </button>
                      </>
                    )}
                  </>
                ) : hasPhoto ? (
                  <button
                    type="button"
                    className={cn(styles.avatarButton, styles.avatarZoom)}
                    aria-label="Открыть фото"
                    onClick={() => setViewerOpen(true)}
                  >
                    {avatar}
                  </button>
                ) : (
                  avatar
                )}
              </div>

              <h1 ref={nameRef} className={styles.name}>
                {fullNameOf(user)}
                {isSelf && (
                  <Badge variant="primary" className={styles.selfTag}>
                    вы
                  </Badge>
                )}
              </h1>
              <div className={styles.headerMore}>
                {status && <p className={cn(styles.status, online && styles.statusOnline)}>{status}</p>}
                {roleChips.length > 0 && (
                  <div className={styles.roles}>
                    {roleChips.map((r) => (
                      <Badge key={r.key}>{r.label}</Badge>
                    ))}
                  </div>
                )}
                {subtitle.length > 0 && <p className={styles.subtitle}>{subtitle}</p>}
                {meta.length > 0 && (
                  <p className={styles.meta}>
                    {meta.map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                  </p>
                )}
              </div>
              {contacts.length > 0 && (
                <div className={styles.contacts}>
                  {contacts.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={styles.contact}
                      title="Скопировать"
                      onClick={() => void copyContact(c.text, c.done)}
                    >
                      <c.icon size={13} aria-hidden className={styles.contactIcon} />
                      <span className={styles.contactText}>{c.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        </Card>

        {ticketStats && kpi && (
          <Tabs value={tab} onValueChange={(v) => setTab(v as 'overview' | 'created' | 'assigned')}>
            { }
            <TabsList className={styles.tabsList}>
              <TabsTab value="overview">Обзор</TabsTab>
              {canSeeHistory && <TabsTab value="created">Созданные</TabsTab>}
              {canSeeHistory && (isStaff || isEngineer) && <TabsTab value="assigned">В работе</TabsTab>}
            </TabsList>

            <TabsPanel value="overview" className={styles.stack}>
              <Card className={styles.overviewCard}>
                <section className={styles.statGroup} aria-labelledby="profile-metrics">
                  <div className={styles.statGroupHead}>
                    <h2 id="profile-metrics" className={styles.statGroupTitle}>
                      Показатели
                    </h2>
                    {isEngineer && (
                      <Link className={styles.inlineLink} to="/kpi">
                        Как считаются очки
                      </Link>
                    )}
                  </div>
                  <div className={styles.metricsBody}>
                  {isEngineer && (
                    <div className={styles.stats}>
                      <Stat
                        icon={KpiIcon}
                        tone="gold"
                        accent
                        label="Очки за месяц"
                        value={num(kpi.monthPoints)}
                        hint={`всего ${num(kpi.totalPoints)}`}
                      />
                      <Stat
                        icon={ResolvedIcon}
                        tone="positive"
                        label="Решено за месяц"
                        value={kpi.monthTicketsClosed.toLocaleString('ru-RU')}
                        hint={`всего ${kpi.totalTicketsClosed.toLocaleString('ru-RU')}`}
                      />
                      <Stat
                        icon={RatingIcon}
                        tone="gold"
                        label="Средняя оценка"
                        value={kpi.avgRating == null ? '—' : kpi.avgRating.toFixed(1)}
                        hint={kpi.ratedCount > 0 ? `оценок: ${kpi.ratedCount}` : 'оценок пока нет'}
                      >
                        {kpi.avgRating != null && <Stars value={kpi.avgRating} />}
                      </Stat>
                    </div>
                  )}
                  <div className={styles.wideStats}>
                    <div className={styles.wideStat}>
                      <div className={styles.statTop}>
                        <ToneIcon icon={TicketIcon} tone="info" />
                        <span className={styles.statLabel}>Создано заявок</span>
                        <span className={styles.wideValue}>{ticketStats.createdTotal.toLocaleString('ru-RU')}</span>
                      </div>
                      <div className={styles.bar} aria-hidden>
                        <span
                          className={styles.barOpen}
                          style={{
                            flexGrow: ticketStats.createdOpen,
                          }}
                        />
                        <span
                          className={styles.barDone}
                          style={{ flexGrow: Math.max(0, ticketStats.createdTotal - ticketStats.createdOpen) }}
                        />
                      </div>
                      <div className={styles.legend}>
                        <span className={styles.legendOpen}>открыто {ticketStats.createdOpen.toLocaleString('ru-RU')}</span>
                        <span className={styles.legendDone}>
                          закрыто {Math.max(0, ticketStats.createdTotal - ticketStats.createdOpen).toLocaleString('ru-RU')}
                        </span>
                      </div>
                    </div>
                    {isEngineer && (
                      <div className={cn(styles.wideStat, styles.wideRing)}>
                        <div className={styles.wideRingBody}>
                          <div className={styles.statTop}>
                            <ToneIcon icon={AssigneeIcon} tone="accent" />
                            <span className={styles.statLabel}>Назначено</span>
                          </div>
                          <span className={styles.statValue}>{ticketStats.assignedTotal.toLocaleString('ru-RU')}</span>
                          <span className={styles.statHint}>
                            решено {ticketStats.assignedSolved.toLocaleString('ru-RU')}
                          </span>
                        </div>
                        <Ring part={ticketStats.assignedSolved} total={ticketStats.assignedTotal} />
                      </div>
                    )}
                  </div>
                  </div>
                </section>
              </Card>

              {grants.length > 0 && (
                <Card padded={false}>
                  <CardHeader className={styles.cardHead}>
                    <CardTitle>Доступы</CardTitle>
                    <p className={styles.cardHint}>
                      Папки, к которым доступ выдавался по заявкам. Журнал ведут инженеры вручную,
                      поэтому он может быть неполным.
                    </p>
                  </CardHeader>
                  <CardBody>
                    <ul className={styles.grantList}>
                      {grants.map((grant) => (
                        <li
                          key={grant.id}
                          className={cn(
                            styles.grantItem,
                            (grant.expired || grant.revokedAt) && styles.grantInactive,
                          )}
                        >
                          <span className={styles.grantPath} title={grant.path}>
                            {grant.path}
                          </span>
                          <span className={styles.grantMeta}>
                            {FOLDER_ACCESS_LABELS[grant.accessType]}
                            {grant.validUntil &&
                              ` · до ${new Date(grant.validUntil).toLocaleDateString('ru-RU')}`}
                            {grant.expired && ' · срок вышел'}
                            {grant.revokedAt && ' · отозван'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              )}
            </TabsPanel>

            { }
            {canSeeHistory && <TabsPanel value="created">{ticketList}</TabsPanel>}
            {canSeeHistory && <TabsPanel value="assigned">{ticketList}</TabsPanel>}
          </Tabs>
        )}

        {viewerOpen && user.avatarUrl && (
          <MediaViewer
            items={[{ url: resolveApiUrl(user.avatarUrl), fileName: fullNameOf(user) }]}
            index={0}
            onIndexChange={() => undefined}
            onClose={() => setViewerOpen(false)}
          />
        )}
        <AvatarCropDialog file={cropFile} onClose={() => setCropFile(null)} onSave={saveAvatar} />
      </main>
    </PageMotion>
  );
};
